import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { createMutation } from 'react-query-kit';
import { useSelector } from 'react-redux';

import { EcoBankingAxiosClientAuthedInstance } from 'api';
import endpoints from 'api/CompeonReverseApi/endpoints';
import { CbBankBeneficialOwner } from 'cbbank/inquiry/fields';
import { translations } from 'new/form/common/types';
import { BeneficialOwner } from 'pages/operationPortal/CompaniesDetails/AssiciatedPerson/components/beneficialOwners/fields';
import { useToasts } from 'shared/hooks/useToasts';
import { getInquiryIdSelector } from 'store/inquiryDetails/selectors';
import { useTranslations } from 'utils/hooks/useTranslations';

import {
  mapBeneficialOwnerToBackend,
  mapCbBankBeneficialOwnerToBackend,
} from '../../components/beneficialOwners/mappers';
import { ASSOCIATED_PEOPLE_QUERY_KEY } from '../useFetchAssociatedPeople';

const useCreateDzbBeneficialOwnerMutation = createMutation({
  mutationFn: async (variables: { inquiryId: string; beneficialOwner: BeneficialOwner }) => {
    return EcoBankingAxiosClientAuthedInstance.post(
      endpoints.INQUIRIES.SPECIALIZED.DZB.BENEFICIAL_OWNERS.CREATE.compose({
        params: {
          id: variables.inquiryId,
        },
      }),
      {
        data: {
          attributes: mapBeneficialOwnerToBackend(variables.beneficialOwner),
        },
      },
    );
  },
});

export function useCreateDzbBeneficialOwner() {
  const inquiryId = useSelector(getInquiryIdSelector);
  const toasts = useToasts();
  const t = useTranslations();
  const { successMessage, errorMessage } =
    translations.pages.companiesDetails.associatedPerson.new.beneficialOwners.actions.add;
  const queryClient = useQueryClient();

  const mutation = useCreateDzbBeneficialOwnerMutation({
    onSuccess: () => {
      toasts.success({ description: t(successMessage) });
      queryClient.invalidateQueries([ASSOCIATED_PEOPLE_QUERY_KEY]);
    },
    onError: () => {
      toasts.error({ description: t(errorMessage) });
    },
  });

  const mutateAsync = useCallback(
    (beneficialOwner: BeneficialOwner) => {
      return mutation.mutateAsync({
        inquiryId,
        beneficialOwner,
      });
    },
    [mutation, inquiryId],
  );

  return {
    ...mutation,
    mutateAsync,
  };
}

const useCreateCbBankBeneficialOwnerMutation = createMutation({
  mutationFn: async (variables: { inquiryId: string; beneficialOwner: CbBankBeneficialOwner }) => {
    return EcoBankingAxiosClientAuthedInstance.post(
      endpoints.INQUIRIES.SPECIALIZED.CB_BANK.BENEFICIAL_OWNERS.CREATE.compose({
        params: {
          id: variables.inquiryId,
        },
      }),
      {
        data: {
          attributes: mapCbBankBeneficialOwnerToBackend(variables.beneficialOwner),
        },
      },
    );
  },
});

export function useCreateCbBankBeneficialOwner() {
  const inquiryId = useSelector(getInquiryIdSelector);
  const toasts = useToasts();
  const t = useTranslations();
  const { successMessage, errorMessage } =
    translations.pages.companiesDetails.associatedPerson.new.beneficialOwners.actions.add;
  const queryClient = useQueryClient();

  const mutation = useCreateCbBankBeneficialOwnerMutation({
    onSuccess: () => {
      toasts.success({ description: t(successMessage) });
      queryClient.invalidateQueries([ASSOCIATED_PEOPLE_QUERY_KEY]);
    },
    onError: () => {
      toasts.error({ description: t(errorMessage) });
    },
  });

  const mutateAsync = useCallback(
    (beneficialOwner: CbBankBeneficialOwner) => {
      return mutation.mutateAsync({
        inquiryId,
        beneficialOwner,
      });
    },
    [mutation, inquiryId],
  );

  return {
    ...mutation,
    mutateAsync,
  };
}
